import { CornerDownRightIcon, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import useRenewalEmployees from 'apps/employer_renewals/hooks/useRenewalEmployees'
import { useEmployer } from 'apps/employer_renewals/providers/EmployerProvider'
import { useEmployerRenewalConfig } from 'apps/employer_renewals/providers/EmployerRenewalConfigProvider'
import { FormDateFieldDS, FormNumberFieldDS } from 'components/reusable/form/FormFields'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'components/ui/sheet'
import { Banner, Button } from 'slab'
import { commaSeparatedListWithAnd } from 'utils'
import { useGetQuotingProspectsProspectIdCensusCensusIdEmployeesId as useGetProspectEmployee } from 'utils/web_api'
import { type PutQuotingProspectsProspectIdCensusCensusIdEmployeesId } from 'utils/web_api.schemas'

export default function EditEmployeeSheet (
  { prospectEmployeeId, onClose, onSuccess, autoNavigateToNext }:
  {
    prospectEmployeeId: string | undefined,
    onClose: () => void,
    onSuccess: () => void,
    autoNavigateToNext: boolean
  }
) {
  // Keep the displayed employee in local state to maintain it during closing animation
  const [displayedEmployee, setDisplayedEmployee] = useState<typeof employee | undefined>(undefined)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  const { updateEmployee, updateEmployeeIsPending, isLoading } = useRenewalEmployees({})
  const { employerIsALE } = useEmployer()
  const { employerRenewalConfig } = useEmployerRenewalConfig()

  const { data: employee, isLoading: isLoadingEmployee } = useGetProspectEmployee(
    employerRenewalConfig?.prospect_id ?? '',
    employerRenewalConfig?.employee_census_id ?? '',
    prospectEmployeeId!,
    {
      query: {
        enabled: !!employerRenewalConfig?.prospect_id && !!employerRenewalConfig?.employee_census_id && !!prospectEmployeeId,
      },
    })

  // Update displayed employee when employee data is fetched
  useEffect(() => {
    if (employee) {
      setDisplayedEmployee(employee)
    }
  }, [employee])

  const missingRequiredInfo = useMemo(() => {
    if (!displayedEmployee) return []
    const missing = []
    if (!displayedEmployee.date_of_birth) missing.push('date of birth')
    if (!displayedEmployee.zip) missing.push('zip code')
    if (!displayedEmployee.yearly_pay && !!employerIsALE) missing.push('annual salary')
    return missing
  }, [displayedEmployee, employerIsALE])

  // Capture the initial missing info state when form is opened to prevent optimistic updates from changing the banner
  const [initialMissingRequiredInfo, setInitialMissingRequiredInfo] = useState(missingRequiredInfo)

  useEffect(() => {
    // Only update initial missing info when not pending (i.e., when employee changes or on mount)
    if (!updateEmployeeIsPending) {
      setInitialMissingRequiredInfo(missingRequiredInfo)
      if (missingRequiredInfo.includes('date of birth')) {
        methods.setFocus('date_of_birth')
      } else if (missingRequiredInfo.includes('zip code')) {
        methods.setFocus('zip')
      } else if (missingRequiredInfo.includes('annual salary')) {
        methods.setFocus('yearly_pay')
      }
    }
  }, [missingRequiredInfo, updateEmployeeIsPending])

  const methods = useForm<PutQuotingProspectsProspectIdCensusCensusIdEmployeesId>({
    defaultValues: {
      yearly_pay: displayedEmployee?.yearly_pay,
      date_of_birth: displayedEmployee?.date_of_birth,
      zip: displayedEmployee?.zip,
    }
  })

  // Update form values when displayed employee changes
  useEffect(() => {
    if (displayedEmployee) {
      methods.reset({
        yearly_pay: displayedEmployee.yearly_pay,
        date_of_birth: displayedEmployee.date_of_birth,
        zip: displayedEmployee.zip,
      })
    } else {
      methods.reset({
        yearly_pay: undefined,
        date_of_birth: undefined,
        zip: undefined,
      })
    }
  }, [displayedEmployee, methods])

  const onSubmit: SubmitHandler<PutQuotingProspectsProspectIdCensusCensusIdEmployeesId> = async (data) => {
    if (!employee?.id) return

    setErrorMessage(undefined)
    await updateEmployee(employee.id, {
      yearly_pay: data.yearly_pay,
      date_of_birth: data.date_of_birth,
      zip: data.zip,
      status: 'finalized',
    }).then(() => {
      toast.success(`Employee ${displayedEmployee?.name ? ` ${displayedEmployee.name}` : ''} information updated.`)
      onSuccess()
    }).catch((error) => {
      setErrorMessage(error.message || error.errors?.join(', ') || 'Unknown error')
    })
  }

  const onOpenChange = (open: boolean) => {
    if (!open) {
      methods.reset({
        yearly_pay: undefined,
        date_of_birth: undefined,
        zip: undefined,
      })
      setErrorMessage(undefined)
      onClose()
      setTimeout(() => setDisplayedEmployee(undefined), 500)
    }
  }

  return (
    <Sheet open={prospectEmployeeId !== undefined} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>
            {isLoadingEmployee
              ? <SkeletonItem heightClass='h-7' widthClass='w-48' />
              : `Edit ${displayedEmployee?.name || ''}`}
          </SheetTitle>
        </SheetHeader>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} className='space-y-4 mt-6'>
            {isLoadingEmployee
              ? <SkeletonItem heightClass='h-12' widthClass='w-full' />
              : (
                  initialMissingRequiredInfo.length === 0
                    ? <Banner type='success' title='Employee has all required information' />
                    : (
                      <Banner type='warning' title='Missing required information'>
                        <p>Please provide their {commaSeparatedListWithAnd(initialMissingRequiredInfo)} in order to mark them as eligible for {employerRenewalConfig?.renewal_year} coverage.</p>
                      </Banner>
                      )
                )}

            <FormDateFieldDS<PutQuotingProspectsProspectIdCensusCensusIdEmployeesId>
              fieldId='date_of_birth'
              fieldLabel='Date of Birth *'
              register={methods.register}
              disabled={isLoadingEmployee}
              validationRule={{ required: 'Date of Birth is required' }}
              error={methods.formState.errors.date_of_birth}
            />

            <FormNumberFieldDS<PutQuotingProspectsProspectIdCensusCensusIdEmployeesId>
              fieldId='zip'
              fieldLabel='ZIP Code *'
              register={methods.register}
              disabled={isLoadingEmployee}
              validationRule={{ required: 'ZIP Code is required' }}
              error={methods.formState.errors.zip}
            />

            <FormNumberFieldDS<PutQuotingProspectsProspectIdCensusCensusIdEmployeesId>
              fieldId='yearly_pay'
              fieldLabel={`Annual Salary ($) ${employerIsALE ? '*' : ''}`}
              register={methods.register}
              disabled={isLoadingEmployee}
              validationRule={{ required: employerIsALE ? 'Annual Salary is required' : false }}
              error={methods.formState.errors.yearly_pay}
            />

            {errorMessage && (
              <Banner type='critical' title='Error updating employee'>
                {errorMessage}
              </Banner>
            )}

            <div className='flex items-center w-full gap-2 pt-4'>
              <div className='flex-1'>
                <Button
                  type='submit'
                  variant='primary'
                  disabled={updateEmployeeIsPending || isLoading || isLoadingEmployee}
                  width='full'
                >
                  <div className='flex items-center space-x-2'>
                    <span>{autoNavigateToNext ? 'Save and next' : 'Save'}</span>
                    {(updateEmployeeIsPending || isLoading || isLoadingEmployee) && <Loader2 className='h-4 w-4 animate-spin' />}
                    {!updateEmployeeIsPending && !isLoading && !isLoadingEmployee && autoNavigateToNext && <CornerDownRightIcon className='h-4 w-4' />}
                  </div>
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  )
}
